"use client";

import React from "react";
import { Instagram, Facebook, Video, Mail, Phone, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="12262" className="flex flex-col gap-4 w-full items-center md:items-start">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-24 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <nav className="flex flex-col gap-3 w-full items-center md:items-start">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="w-full flex justify-center md:justify-start">
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary hover:pl-2 transition-all duration-300 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2 group w-fit">
            {item.label}
            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>)}
    </nav>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <footer id="footer" className="dark bg-[#0a0b0d] text-foreground border-t border-border/10 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[20%] w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[20%] w-[400px] h-[400px] bg-secondary/5 rounded-lg blur-[100px]" />
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        {}
        <div className="flex justify-center mb-16">
          <div className="relative group">
            <div className="absolute -inset-4 bg-primary/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <img src="https://desired-piranha-1.10web.cloud/wp-content/uploads/2026/05/CNCWise-Logo-PNG-2-scaled.png" alt="CNCWise Logo" className="h-12 md:h-14 w-auto object-contain relative z-10" />
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-16">
          {}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <h3 className="text-sm font-black tracking-[0.3em] uppercase mb-6 text-foreground/90 border-b-2 border-primary/40 pb-2 inline-block">
              Platform
            </h3>
            <div className="flex flex-col gap-1 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">
              <div>TROUBLESHOOT</div>
              <div>REPAIR</div>
              <div>MAINTAIN</div>
            </div>
            <MenuProvider menu_id="footer_menu" className="w-full">
              <FooterMenu />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col items-center text-center">
            <h3 className="text-sm font-black tracking-[0.3em] uppercase mb-6 text-foreground/90 border-b-2 border-secondary/40 pb-2 inline-block">
              Connect
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[{
              icon: Instagram,
              label: "Instagram",
              href: "https://instagram.com/cncwise",
              color: "hover:text-primary hover:shadow-[0_0_20px_rgba(34,197,94,0.4)]"
            }, {
              icon: Video,
              label: "TikTok",
              href: "https://tiktok.com/@cncwise",
              color: "hover:text-primary hover:shadow-[0_0_20px_rgba(34,197,94,0.4)]"
            }].map((social, i) => <div key={social.label} data-index={i}>
                  <Button variant="ghost" size="icon" asChild className={`h-12 w-12 rounded-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 ${social.color}`}>
                    <Link to={social.href} newTab aria-label={social.label}>
                      <social.icon className="w-5 h-5" />
                    </Link>
                  </Button>
                </div>)}
            </div>
          </div>

          {}
          <div className="flex flex-col items-center md:items-end text-center md:text-right">
            <h3 className="text-sm font-black tracking-[0.3em] uppercase mb-6 text-foreground/90 border-b-2 border-destructive/40 pb-2 inline-block">
              Support
            </h3>
            <div className="flex flex-col gap-4 items-center md:items-end w-full">
              <Link to="mailto:support@cncwise.com" className="group flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors duration-300 justify-center md:justify-end w-full">
                <span className="text-xs font-mono tracking-wider">support@cncwise.com</span>
                <div className="p-2 rounded-sm bg-white/5 group-hover:bg-primary/20 transition-colors">
                  <Mail className="w-4 h-4" />
                </div>
              </Link>
              <Link to="tel:3038170908" className="group flex items-center gap-3 text-muted-foreground hover:text-secondary transition-colors duration-300 justify-center md:justify-end w-full">
                <span className="text-xs font-mono tracking-wider">303-817-0908</span>
                <div className="p-2 rounded-sm bg-white/5 group-hover:bg-secondary/20 transition-colors">
                  <Phone className="w-4 h-4" />
                </div>
              </Link>
            </div>
          </div>
        </div>

        <Separator className="h-[3px] bg-gradient-to-r from-primary via-secondary to-destructive border-none mb-8 opacity-80" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] text-muted-foreground uppercase tracking-[0.25em] font-bold">
          <p>© {currentYear} CNCWise. Industrial Intelligence Since 1980.</p>
          <p className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-white to-secondary animate-pulse">
            ENGINEERED FOR UPTIME
          </p>
        </div>
      </div>
    </footer>;
}